import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Trash2, Plus, LogOut, Edit2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'mt_blog_admin_token';

const emptyForm = {
  slug: '',
  title: '',
  excerpt: '',
  cover_image: '',
  body: '',
  linkedin_url: '',
  published_at: '',
  author: 'Charu Verma',
  tags: '',
};

export const BlogAdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingSlug, setEditingSlug] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/blog/upload-image`, {
        method: 'POST',
        headers: { 'X-Admin-Token': token },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await res.json();
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Verify token on mount if present
  useEffect(() => {
    const verify = async () => {
      if (!token) return;
      setChecking(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/blog/verify`, {
          method: 'POST',
          headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (res.ok) {
          setAuthed(true);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setToken('');
        }
      } catch {
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, [token]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/blog/posts?limit=200`);
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (err) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) fetchPosts();
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/verify`, {
        method: 'POST',
        headers: { 'X-Admin-Token': tokenInput.trim(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (res.ok) {
        localStorage.setItem(TOKEN_KEY, tokenInput.trim());
        setToken(tokenInput.trim());
        setAuthed(true);
        toast.success('Signed in');
      } else {
        toast.error('Invalid token');
      }
    } catch {
      toast.error('Verification failed');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setAuthed(false);
    setForm(emptyForm);
    setEditingSlug(null);
  };

  const handleEdit = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      if (!res.ok) throw new Error('Failed to load post');
      const data = await res.json();
      const p = data.post || {};
      setForm({
        slug: p.slug || '',
        title: p.title || '',
        excerpt: p.excerpt || '',
        cover_image: p.cover_image || '',
        body: p.body || '',
        linkedin_url: p.linkedin_url || '',
        published_at: p.published_at ? p.published_at.slice(0, 10) : '',
        author: p.author || 'Charu Verma',
        tags: (p.tags || []).join(', '),
      });
      setEditingSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Failed to load post for editing');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/blog/posts/${slug}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Token': token },
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Post deleted');
      if (editingSlug === slug) {
        setForm(emptyForm);
        setEditingSlug(null);
      }
      fetchPosts();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        // Ensure ISO if a date was set
        published_at: form.published_at
          ? new Date(form.published_at).toISOString()
          : undefined,
      };
      const url = editingSlug
        ? `${API_URL}/api/admin/blog/posts/${editingSlug}`
        : `${API_URL}/api/admin/blog/posts`;
      const method = editingSlug ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'X-Admin-Token': token, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(editingSlug ? 'Post updated' : 'Post created');
      setForm(emptyForm);
      setEditingSlug(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ------- Sign-in gate -------
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8] flex items-center justify-center px-6">
        <div className="w-full max-w-md p-8 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15">
          <h1 className="font-display text-2xl text-[#e8dcc8] mb-2 text-center">Blog Admin</h1>
          <p className="font-body text-sm text-[#d4c4a8]/60 mb-6 text-center">
            Enter your admin token to manage blog posts.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              placeholder="Admin token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
              data-testid="admin-token-input"
              autoFocus
            />
            <button
              type="submit"
              disabled={checking || !tokenInput.trim()}
              className="w-full h-11 rounded-xl bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest text-sm disabled:opacity-50 hover:scale-[1.02] transition-transform"
              data-testid="admin-signin-btn"
            >
              {checking ? 'Verifying…' : 'Sign In'}
            </button>
          </form>
          <button
            onClick={() => navigate('/')}
            className="mt-6 text-xs text-[#d4c4a8]/50 hover:text-[#e89b47] transition-colors block mx-auto font-body"
          >
            ← Back to home
          </button>
        </div>
      </div>
    );
  }

  // ------- Admin dashboard -------
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f1f35] to-[#0a1628] text-[#e8dcc8]">
      <div className="max-w-6xl mx-auto px-6 pt-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          View Blog
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 text-[#d4c4a8]/60 hover:text-[#e89b47] transition-colors font-body text-sm"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl text-[#e8dcc8] mb-8">Blog Admin</h1>

        {/* Editor */}
        <section className="mb-12 p-6 sm:p-8 rounded-2xl bg-[#1e2a4a]/50 border border-[#d4c4a8]/15">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-xl text-[#e8dcc8]">
              {editingSlug ? `Edit: ${editingSlug}` : 'New Post'}
            </h2>
            {editingSlug && (
              <button
                type="button"
                onClick={() => { setForm(emptyForm); setEditingSlug(null); }}
                className="text-xs text-[#d4c4a8]/60 hover:text-[#e89b47] font-body"
                data-testid="admin-new-post-btn"
              >
                <Plus className="w-3 h-3 inline mr-1" /> New Post
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Title *</label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                data-testid="admin-title-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Slug (URL)</label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="auto-generated from title"
                  disabled={!!editingSlug}
                  className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm disabled:opacity-50"
                  data-testid="admin-slug-input"
                />
              </div>
              <div>
                <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Published Date</label>
                <input
                  type="date"
                  value={form.published_at}
                  onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                  data-testid="admin-date-input"
                />
              </div>
            </div>

            <div>
              <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Cover Image</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleImageUpload(e.dataTransfer.files?.[0]); }}
                className="mb-3 flex items-center justify-center gap-3 px-4 py-6 rounded-lg border-2 border-dashed border-[#d4c4a8]/25 hover:border-[#e89b47]/60 bg-[#0a1628]/60 cursor-pointer transition-colors"
                data-testid="admin-cover-dropzone"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-[#e89b47]" />
                    <span className="font-body text-sm text-[#d4c4a8]/70">Uploading…</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="w-4 h-4 text-[#e89b47]" />
                    <span className="font-body text-sm text-[#d4c4a8]/70">Drag &amp; drop an image here, or click to browse (max 5MB)</span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(e) => { handleImageUpload(e.target.files?.[0]); e.target.value = ''; }}
                data-testid="admin-cover-file-input"
              />
              {form.cover_image && (
                <div className="mb-3 flex items-center gap-3">
                  <img
                    src={form.cover_image}
                    alt="Cover preview"
                    className="h-20 w-32 object-cover rounded-lg border border-[#d4c4a8]/20"
                    data-testid="admin-cover-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_image: '' })}
                    className="text-xs text-red-300/70 hover:text-red-400 font-body"
                    data-testid="admin-cover-remove-btn"
                  >
                    Remove
                  </button>
                </div>
              )}
              <input
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                placeholder="…or paste an image URL (https://…)"
                className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                data-testid="admin-cover-input"
              />
            </div>

            <div>
              <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Excerpt (short summary)</label>
              <textarea
                rows={2}
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm resize-y"
                data-testid="admin-excerpt-input"
              />
            </div>

            <div>
              <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Body *</label>
              <p className="text-xs text-[#d4c4a8]/50 mb-2 font-body">Paste plain text (paragraphs separated by blank lines) or HTML.</p>
              <textarea
                required
                rows={14}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm resize-y"
                data-testid="admin-body-input"
              />
            </div>

            <div>
              <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Tags (comma-separated)</label>
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="branding, storytelling, strategy"
                className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                data-testid="admin-tags-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">LinkedIn URL (original)</label>
                <input
                  value={form.linkedin_url}
                  onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                  placeholder="https://www.linkedin.com/pulse/…"
                  className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                  data-testid="admin-linkedin-input"
                />
              </div>
              <div>
                <label className="block font-display text-xs tracking-widest text-[#e89b47] uppercase mb-2">Author</label>
                <input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-[#0a1628] border border-[#d4c4a8]/20 text-[#e8dcc8] focus:outline-none focus:border-[#e89b47] font-body text-sm"
                  data-testid="admin-author-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 h-11 px-6 bg-gradient-to-r from-[#e89b47] to-[#d4883a] text-[#0a1628] font-display tracking-widest text-sm rounded-xl shadow-lg shadow-[#e89b47]/25 disabled:opacity-60 hover:scale-[1.02] transition-transform"
              data-testid="admin-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : editingSlug ? 'Update Post' : 'Publish Post'}
            </button>
          </form>
        </section>

        {/* Posts list */}
        <section>
          <h2 className="font-display text-xl text-[#e8dcc8] mb-4">All Posts ({posts.length})</h2>
          {loading && (
            <div className="flex items-center gap-2 text-[#d4c4a8]/60 font-body text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && posts.length === 0 && (
            <p className="font-body text-sm text-[#d4c4a8]/60">No posts yet. Add your first one above.</p>
          )}
          <div className="space-y-3" data-testid="admin-posts-list">
            {posts.map((p) => (
              <div
                key={p.slug}
                className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-[#1e2a4a]/40 border border-[#d4c4a8]/10"
                data-testid={`admin-post-row-${p.slug}`}
              >
                <div className="flex-1 min-w-[220px]">
                  <p className="font-display text-sm text-[#e8dcc8]">{p.title}</p>
                  <p className="font-body text-xs text-[#d4c4a8]/50 mt-1">
                    /blog/{p.slug} · {(p.published_at || '').slice(0, 10)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(p.slug)}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-display tracking-widest text-[#d4c4a8] border border-[#d4c4a8]/20 hover:border-[#e89b47] hover:text-[#e89b47] transition-colors"
                    data-testid={`admin-edit-${p.slug}`}
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.slug)}
                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-display tracking-widest text-red-300/70 border border-red-300/20 hover:border-red-400 hover:text-red-400 transition-colors"
                    data-testid={`admin-delete-${p.slug}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogAdminPage;
